import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import {
  detectHotkeyPlatform,
  formatKeySteps,
  formatKeys,
  isEditableTarget,
  keyPressFromEvent,
  matchesCombo,
  type FormatKeysOptions,
  type HotkeyPlatform,
  type KeyPress,
} from './keys';
import {
  SHORTCUT_GROUPS,
  shortcutRegistry,
  type RegisteredShortcut,
  type ShortcutDefinition,
  type ShortcutGroup,
  type ShortcutRegistry,
} from './registry';

export interface HotkeysContextValue {
  registry: ShortcutRegistry;
  platform: HotkeyPlatform;
  /** `formatKeys` bound to this provider's platform. */
  format: (keys: string, options?: Omit<FormatKeysOptions, 'platform'>) => string;
  /** `formatKeySteps` bound to this provider's platform. */
  formatSteps: (keys: string, options?: Omit<FormatKeysOptions, 'platform'>) => string[];
}

const HotkeysContext = createContext<HotkeysContextValue | null>(null);

export interface HotkeysProviderProps {
  /** Defaults to the module-level `shortcutRegistry`. */
  registry?: ShortcutRegistry;
  /** Defaults to the detected platform; set it for deterministic rendering in tests. */
  platform?: HotkeyPlatform;
  /** Max gap between keys of a sequence. Default 1000 ms. */
  sequenceTimeoutMs?: number;
  /** Where the keydown listener attaches. Default `document`. */
  target?: Pick<Document, 'addEventListener' | 'removeEventListener'>;
  children?: ReactNode;
}

function findMatch(
  entries: readonly RegisteredShortcut[],
  presses: KeyPress[],
  platform: HotkeyPlatform,
  editable: boolean,
): { exact: RegisteredShortcut | undefined; prefix: boolean } {
  let exact: RegisteredShortcut | undefined;
  let prefix = false;
  for (let index = entries.length - 1; index >= 0; index -= 1) {
    const entry = entries[index];
    if (!entry) continue;
    const { definition } = entry;
    if (editable && !definition.allowInInputs) continue;
    if (definition.when && !definition.when()) continue;
    for (const sequence of entry.sequences) {
      if (sequence.length < presses.length) continue;
      const matchesSoFar = presses.every((press, step) => {
        const combo = sequence[step];
        return combo !== undefined && matchesCombo(press, combo, platform);
      });
      if (!matchesSoFar) continue;
      if (sequence.length === presses.length) exact ??= entry;
      else prefix = true;
    }
    if (exact) break;
  }
  return { exact, prefix };
}

/**
 * Owns the single keydown listener that drives every registered shortcut. Registrations come from
 * `registerShortcut` (feature modules at startup) and `useHotkey` (screens while mounted); the most
 * recent registration wins a conflict, so a page can shadow a global key while it is open. A sequence
 * (`g s`) must complete within `sequenceTimeoutMs`; typing in an input never triggers a shortcut unless
 * it opted in with `allowInInputs`.
 */
export function HotkeysProvider({
  registry = shortcutRegistry,
  platform,
  sequenceTimeoutMs = 1000,
  target,
  children,
}: HotkeysProviderProps) {
  const resolvedPlatform = platform ?? detectHotkeyPlatform();
  const value = useMemo<HotkeysContextValue>(
    () => ({
      registry,
      platform: resolvedPlatform,
      format: (keys, options) => formatKeys(keys, { ...options, platform: resolvedPlatform }),
      formatSteps: (keys, options) => formatKeySteps(keys, { ...options, platform: resolvedPlatform }),
    }),
    [registry, resolvedPlatform],
  );

  useEffect(() => {
    const listenerTarget = target ?? (typeof document === 'undefined' ? undefined : document);
    if (!listenerTarget) return;
    let pending: KeyPress[] = [];
    let timer: ReturnType<typeof setTimeout> | undefined;
    const clear = () => {
      pending = [];
      if (timer) clearTimeout(timer);
      timer = undefined;
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      const press = keyPressFromEvent(event);
      if (!press) return;
      const editable = isEditableTarget(event.target);
      const entries = registry.list();
      let presses = [...pending, press];
      let result = findMatch(entries, presses, resolvedPlatform, editable);
      if (!result.exact && !result.prefix && pending.length > 0) {
        // The sequence went nowhere; the key may still start a new one.
        presses = [press];
        result = findMatch(entries, presses, resolvedPlatform, editable);
      }
      if (result.exact) {
        event.preventDefault();
        clear();
        result.exact.definition.handler(event);
        return;
      }
      if (result.prefix) {
        pending = presses;
        if (timer) clearTimeout(timer);
        timer = setTimeout(clear, sequenceTimeoutMs);
        return;
      }
      clear();
    };
    listenerTarget.addEventListener('keydown', onKeyDown as EventListener);
    return () => {
      listenerTarget.removeEventListener('keydown', onKeyDown as EventListener);
      clear();
    };
  }, [registry, resolvedPlatform, sequenceTimeoutMs, target]);

  return <HotkeysContext.Provider value={value}>{children}</HotkeysContext.Provider>;
}

let fallbackContext: HotkeysContextValue | undefined;

/** The registry and platform in scope. Outside a provider: the default registry, with no listener attached. */
export function useHotkeys(): HotkeysContextValue {
  const context = useContext(HotkeysContext);
  if (context) return context;
  fallbackContext ??= {
    registry: shortcutRegistry,
    platform: detectHotkeyPlatform(),
    format: (keys, options) => formatKeys(keys, options),
    formatSteps: (keys, options) => formatKeySteps(keys, options),
  };
  return fallbackContext;
}

export interface UseHotkeyOptions {
  /** Defaults to a per-instance id; set one for a shortcut other code refers to. */
  id?: string;
  /** Without a label the shortcut is not listed in the `?` sheet. */
  label?: string;
  group?: ShortcutGroup;
  when?: () => boolean;
  allowInInputs?: boolean;
  /** Unregisters while false. Default true. */
  enabled?: boolean;
  hidden?: boolean;
}

/**
 * Registers a shortcut for as long as the component is mounted. The handler and `when` are read
 * through refs, so a new closure on every render does not re-register.
 */
export function useHotkey(
  keys: string | readonly string[],
  handler: (event: KeyboardEvent) => void,
  options: UseHotkeyOptions = {},
): void {
  const { registry } = useHotkeys();
  const generatedId = useId();
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  const whenRef = useRef(options.when);
  whenRef.current = options.when;
  const { id = generatedId, label, group = 'Global', allowInInputs = false, enabled = true, hidden } = options;
  const keysJson = JSON.stringify(typeof keys === 'string' ? [keys] : keys);

  useEffect(() => {
    if (!enabled) return;
    const definition: ShortcutDefinition = {
      id,
      keys: JSON.parse(keysJson) as string[],
      label: label ?? '',
      group,
      handler: (event) => handlerRef.current(event),
      when: () => whenRef.current?.() ?? true,
      allowInInputs,
      hidden: hidden ?? label === undefined,
    };
    return registry.register(definition);
  }, [registry, id, keysJson, label, group, allowInInputs, enabled, hidden]);
}

export interface ShortcutListItem {
  id: string;
  /** Every alternative, as written. */
  keys: readonly string[];
  /** The alternatives as display strings (`⌘K`, `G then S`). */
  display: readonly string[];
  label: string;
}

export interface ShortcutListGroup {
  group: ShortcutGroup;
  shortcuts: ShortcutListItem[];
}

/** The listable shortcuts by group, in the fixed group order, for the `?` sheet. Reactive to registrations. */
export function useShortcutList(): ShortcutListGroup[] {
  const { registry, platform } = useHotkeys();
  const subscribe = useCallback((callback: () => void) => registry.subscribe(callback), [registry]);
  const entries = useSyncExternalStore(subscribe, registry.list, registry.list);
  return useMemo(() => {
    const groups = new Map<ShortcutGroup, ShortcutListItem[]>();
    for (const { definition } of entries) {
      if (definition.hidden || !definition.label) continue;
      const keys = typeof definition.keys === 'string' ? [definition.keys] : [...definition.keys];
      const item: ShortcutListItem = {
        id: definition.id,
        keys,
        display: keys.map((k) => formatKeys(k, { platform })),
        label: definition.label,
      };
      const bucket = groups.get(definition.group) ?? [];
      bucket.push(item);
      groups.set(definition.group, bucket);
    }
    return SHORTCUT_GROUPS.filter((group) => groups.has(group)).map((group) => ({
      group,
      shortcuts: groups.get(group) ?? [],
    }));
  }, [entries, platform]);
}
